import React, { useRef } from "react";
import { Search, X } from "lucide-react";
import "./FilterBar.css";

interface FilterBarProps {
    /** Immediate input value — keep the caller's state undebounced so typing stays snappy. */
    value: string;
    onChange: (next: string) => void;
    placeholder: string;
    /** Accessible name for the field; the placeholder alone is not a label. */
    label: string;
    /** Rows surviving the active filter. Ignored while `active` is false. */
    matches: number;
    /** Unfiltered row count for this region — never changes as the term is typed. */
    total: number;
    /** True when anything is narrowing the region: a term, or a scope chip. */
    active: boolean;
    /** Right-aligned controls — Expand all / Collapse all buttons, or scope chips. */
    children?: React.ReactNode;
}

/**
 * Inline filter bar for one card's list. Sits as the first child of the card it filters,
 * directly under the card header, so the pairing is unambiguous and the card's own metrics
 * stay clearly outside its scope. Purely presentational: matching, counting and any
 * auto-expansion live with the page that owns the data.
 */
const FilterBar: React.FC<FilterBarProps> = ({
    value, onChange, placeholder, label, matches, total, active, children,
}) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const clear = () => {
        onChange("");
        inputRef.current?.focus();
    };

    return (
        <div className={`filter-bar${active ? " active" : ""}`}>
            <label className="fb-input">
                <Search size={15} className="fb-ic" aria-hidden />
                <input
                    ref={inputRef}
                    type="text"
                    value={value}
                    placeholder={placeholder}
                    aria-label={label}
                    autoComplete="off"
                    spellCheck={false}
                    onChange={(e) => onChange(e.target.value)}
                />
                {value !== "" && (
                    <button
                        type="button"
                        className="fb-x"
                        title="Clear filter"
                        aria-label="Clear filter"
                        onClick={clear}
                    >
                        <X size={13} />
                    </button>
                )}
            </label>

            <span className={`fb-count${active && matches === 0 ? " zero" : ""}`} aria-live="polite">
                {active ? `${matches} of ${total}` : ""}
            </span>

            {children}
        </div>
    );
};

interface FilterEmptyProps {
    /** Plural noun for the region: "tasks", "cost codes", "employees". */
    noun: string;
    /** Active term; empty means only a scope chip is narrowing the list. */
    term: string;
    onClear: () => void;
}

/** Zero-match state. Only shown when a filter is active AND the region has data to begin with. */
export const FilterEmpty: React.FC<FilterEmptyProps> = ({ noun, term, onClear }) => (
    <div className="filter-empty">
        <Search size={26} className="fe-ic" aria-hidden />
        <div className="fe-t">
            {term ? <>No {noun} match “<b>{term}</b>”</> : <>No {noun} match this filter</>}
        </div>
        <div className="fe-s">Try a shorter term, a code, or part of a description.</div>
        <button type="button" className="fe-clear" onClick={onClear}>
            Clear filter
        </button>
    </div>
);

export default FilterBar;
